import { Component, computed, DestroyRef, effect, inject, input, model, OnInit, ViewChild } from '@angular/core';
import { DateTime } from 'luxon';
import { SbDateTime, SbLocalDateTime } from '../../../services/api/api-date-time';
import { XYChartModel } from '@ngx-seatback/charts';
import { API } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { ChartComponent } from '@ngx-seatback/charts';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService } from '../../../services/permission.service';
import { CullDTO, FrameDTO } from '../../../services/api/api';
import { MsOptions } from '../../../services/global/global-interfaces';

@Component({
  selector: 'sb-comp-heatmap-status',
  templateUrl: './heatmap-status.component.html',
  styleUrls: ['./heatmap-status.component.scss'],
  standalone: false,
})
export class HeatmapStatusComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly permissionService = inject(PermissionService);

  @ViewChild('heatmapChart') chart: ChartComponent;
  integratedChart = input(false);
  halfHeight = input(false);
  inputData = input<FrameDTO[] | null>(null);

  from = model<SbLocalDateTime | null>(null);
  till = model<SbLocalDateTime | null>(null);

  timespan = computed(() => {
    const from = this.from();
    const till = this.till();
    if (!from || !till) {
      return '-';
    }
    return (
      DateTime.fromJSDate(this.util.localDateToJSDate(till))
        .diff(DateTime.fromJSDate(this.util.localDateToJSDate(from)), 'days')
        ?.days?.toString() ?? '-'
    );
  });

  heatmapStatusData: XYChartModel = {
    title: 'Heatmap',
    height: 500,
    width: 100,
    legend: false,
    data: [],
    valueAssignments: [],
  };

  interval = 3600; // one hour 60*60

  msCullOptions: MsOptions;
  culls: CullDTO[] = [];
  filteredCulls: CullDTO[] = [];
  deviceIds: string = '';

  constructor(
    private api: API,
    private errorHandler: ErrorService,
    public util: UtilService,
    private translate: TranslateService
  ) {
    effect(() => {
      const frames = this.inputData();
      if (frames) {
        this.heatmapStatusData.loadingDone = false;
        this.initHeatmapStatusChart(frames);
      }
    });
  }

  ngOnInit(): void {
    if (!this.inputData()) {
      this.refreshFromServer();
    }
  }

  refreshFromServer() {
    if (!this.permissionService.hasPermission(this.permissionService.Permission.ss_nm_reporting)) {
      return;
    }
    if (!this.from && !this.till) {
      this.from.set(new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 91 })));
      this.till.set(new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 1 })));
    }
    this.heatmapStatusData.loadingDone = false;
    const deviceIds = this.deviceIds
      .split(',')
      .map(deviceId => Number(deviceId.trim()))
      .filter(deviceId => !isNaN(deviceId));
    const cullIds = this.filteredCulls.map(cull => cull.id);

    this.api
      .ss_nm_Reporting_deviceStatusCountHistory({
        from: new SbDateTime(this.from().value),
        till: new SbDateTime(this.till().value),
        interval: this.interval,
        playerCull: cullIds?.length > 0 ? cullIds : null,
        device: deviceIds?.length > 0 ? deviceIds : null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.initHeatmapStatusChart(data?.buckets?.[0]?.frames ?? []);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  private initHeatmapStatusChart(frames: FrameDTO[]) {
    if (!frames || frames.length === 0) {
      return;
    }

    const valueAssignments = [
      {
        value: 'online',
        valueName: this.translate.instant('lang.globals.online'),
        color: '#2cc361',
        categories: ['date'],
        fill: true,
      },
      {
        value: 'currentlyOffline',
        valueName: this.translate.instant('lang.globals.recOnline'),
        color: '#ffb822',
        categories: ['date'],
        fill: true,
      },
      {
        value: 'plannedOffline',
        valueName: this.translate.instant('lang.globals.plannedOffline'),
        color: '#ff7600',
        categories: ['date'],
        fill: true,
      },
      {
        value: 'offline',
        valueName: this.translate.instant('lang.globals.offline'),
        color: '#c36157',
        categories: ['date'],
        fill: true,
      },
      {
        value: 'unknown',
        valueName: this.translate.instant('lang.globals.unknown'),
        color: '#999dae',
        categories: ['date'],
        fill: true,
      },
    ];

    const valueAssignmentMapping: Record<string, number> = {
      quotaOnlineStatus_Online: 0,
      quotaOnlineStatus_CurrentlyOffline: 1,
      quotaOnlineStatus_PlannedOffline: 2,
      quotaOnlineStatus_Offline: 3,
      quotaOnlineStatus_Unknown: 4,
    };

    const data = frames.map(frame => {
      const quotaKeys = Object.keys(frame.data).filter(qKey => qKey.startsWith('quota'));

      const key = quotaKeys.reduce((maxKey, currentKey) => {
        return frame.data[currentKey] > frame.data[maxKey] ? currentKey : maxKey;
      }, quotaKeys[4]);

      const indexOfAssignment = key ? valueAssignmentMapping[key] : 4;

      return {
        date: frame.range.from.value.toFormat('dd.MM'),
        hour: frame.range.from.value.hour.toString().padStart(2, '0') + ':00 h',
        value: valueAssignments[indexOfAssignment].value,
        valueName: valueAssignments[indexOfAssignment].valueName,
      };
    });

    this.heatmapStatusData.valueAssignments = valueAssignments;
    this.heatmapStatusData.data = data;

    if (this.chart) {
      this.chart.rerenderChart(this.heatmapStatusData);
    }
    this.heatmapStatusData.loadingDone = true;
  }

  handleDateChangeFrom(date: SbLocalDateTime) {
    this.from.set(date);
  }

  handleDateChangeTill(date: SbLocalDateTime) {
    this.till.set(date);
  }

  update($event: any[], type: string) {
    if (type === 'PLAYER_CULLS') {
      if ($event?.length > 0) {
        this.filteredCulls = [...$event];
      } else {
        this.filteredCulls = null;
      }
    }
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'PLAYER_CULLS') {
      if (options) {
        this.msCullOptions = options;
      }
      if (this.msCullOptions?.pagination) {
        this.api
          .cs_Cull_retrieveAll(
            {
              search: this.util.encodeSearchQueryParam(false, this.msCullOptions?.search),
              type: 'PlayerCull',
            },
            this.msCullOptions.pagination
          )
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (culls: CullDTO[]) => {
              this.culls = culls;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }
}
